import { createRoute, Outlet, redirect, useLocation } from "@tanstack/solid-router";
import { rootRoute } from "../__root";
import { api } from "@/lib/api";
import { sessionActor } from "@/lib/session-machine";
import { queryClient } from "@/lib/query-client";
import { appState } from "@fleet/ui";
import { cloneSession, isValidSession, queryKeys } from "@agape/shared";
import { PageTransition } from "@fleet/ui/page-transition";
import { readAccountsStateSync } from "@/lib/accounts";
import { resolveActiveAccountView } from "@/lib/active-account";
import { readTabAccountId } from "@/lib/tab-account";
import { SESSION_CHECK_TIMEOUT_MS } from "@/lib/constants";

/**
 * agape auth wrapper.
 *
 * NOTE (#13 — de-clone): agape deliberately does NOT use the shared
 * `AuthLayout` (the split brand-panel + glass card shell every clone inherited).
 * Instead each screen owns its own `AuthScene` — a single editorial stacked
 * column with the form full-bleed on the ambient background. So this route is a
 * thin wrapper: it only owns the "already authenticated → redirect" gate and the
 * page transition. The background, brand band and scroll container live in
 * `AuthScene`, rendered per-screen.
 */
function AuthLayoutRoute() {
  const location = useLocation();
  appState.markAsReady();

  return (
    <PageTransition routeKey={location().pathname}>
      <Outlet />
    </PageTransition>
  );
}

export const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_auth",
  // If already authenticated, redirect to the app root.
  beforeLoad: async ({ search }) => {
    // Multi-account: `?mode=add` opens /login to ADD another account while the
    // current one stays signed in (Gmail style). The "already authenticated →
    // go home" gate must be skipped here, otherwise the switcher's "Add account"
    // would bounce straight back to the app and you could never add a 2nd login.
    const isAddingAccount = (search as { mode?: string }).mode === "add";
    if (isAddingAccount) {
      return;
    }

    // Per-tab gating: the "already authenticated → go home" gate must reflect
    // THIS tab's active account, NOT the global session machine (which holds the
    // last-authed real user). Without this, clicking "Sign in" from a GUEST tab
    // would bounce straight into the app as the global admin. Auth pages carry no
    // `/u/<n>` of their own, so the originating tab is recovered from the
    // `redirect` search param (e.g. `/u/2/profile` → index 2).
    const redirectTarget = (search as { redirect?: string }).redirect;
    const tabAccountId = readTabAccountId();
    if (tabAccountId !== null) {
      const view = resolveActiveAccountView(
        readAccountsStateSync(),
        tabAccountId,
        sessionActor.getSnapshot().context.session,
      );
      // Guest tab → show /login (do NOT redirect). Real account → bounce home.
      if (view.isAuthenticated) {
        // oxlint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: redirectTarget ?? "/" });
      }
      return;
    }

    // Fast path: check machine state before hitting the network.
    const snapshot = sessionActor.getSnapshot();
    if (!snapshot.matches("priming") && (snapshot.context.session || snapshot.context.isVerified)) {
      if (snapshot.context.session) {
        // oxlint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
      }
      return;
    }

    // Network check — only if machine has no session yet.
    // Use api.GET (not fetchApi) so we can inspect the status without throwing.
    //
    // P23/CC-60 · I1-bis: this `await` MUST NOT be unbounded. A stalled
    // `get-session` (neither a 401 nor a 5xx, which resolve quickly and therefore made the
    // phase-1 probes appear green) left `beforeLoad` unresolved,
    // the router unable to mount the route and the ENTIRE app blank — `innerText`
    // empty at 3/6/10/15/20 s on `/login`, also measured against the production
    // bundle. The sibling `_app` guard already had a ceiling; this one was left
    // without it because the ceiling was a LOCAL literal instead of a shared
    // compartida.
    //
    // The request is NOT canceled: it remains alive and syncs machine + cache when
    // it arrives. If a session turns out to exist, the `BootManager` (SESSION_RESOLVED →
    // `redirecting`) takes the user out of the login screen; the only cost is a
    // flicker, never a blank screen.
    const fetchSession = async () =>
      api.GET("/api/auth/get-session", { params: { query: { jwt: true } } });
    type FetchResult = Awaited<ReturnType<typeof fetchSession>>;

    const syncReply = (reply: FetchResult) => {
      const { data: replyData, error: replyError } = reply;
      // Synchronize the machine and the cache immediately.
      // This prevents the BootManager from seeing a "pending" or stale session
      // during the route transition, which causes redirection loops.
      sessionActor.send({
        type: "SERVER_REPLY",
        session: isValidSession(replyData) ? cloneSession(replyData) : null,
        error: replyError ?? null,
      });
      queryClient.setQueryData(queryKeys.auth.session(), replyData ?? null);
      return reply;
    };

    const inFlight = fetchSession()
      .then(syncReply)
      // Network error — let user try to log in anyway.
      .catch(() => null);

    const timedOut = new Promise<null>((resolve) => {
      setTimeout(() => {
        resolve(null);
      }, SESSION_CHECK_TIMEOUT_MS);
    });

    const result = await Promise.race([inFlight, timedOut]);
    if (!result) {
      // Timeout or network error: render the login page. The late response remains
      // attached above, and the BootManager redirects if a session was returned.
      return;
    }

    // Redirect is thrown OUTSIDE the try/catch so it propagates to TanStack Router.
    // Decide from the session the machine accepted, not from the raw reply: a
    // stale get-session that LOGOUT revoked must not bounce the guest off /login.
    if (sessionActor.getSnapshot().context.session) {
      // oxlint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
    }
    // 401/403, network error, or null session — not authenticated, show login page.
  },
  component: AuthLayoutRoute,
});
